import type { Country } from "./types";

/**
 * ISO2 -> emoji flag (regional indicator symbols). Zero dependencies, but note
 * these do NOT render as flags on Windows/Chrome (they show "NL", "US", etc).
 * If you need cross-platform flags, pass a `renderFlag` prop that returns an
 * <img>/<svg> from a library like `country-flag-icons` or `flag-icons`.
 */
export function flagEmoji(iso2: string): string {
  return iso2
    .toUpperCase()
    .replace(/[A-Z]/g, (ch) =>
      String.fromCodePoint(0x1f1e6 - 65 + ch.charCodeAt(0))
    );
}

/**
 * If the entry starts with an international prefix (`+…` or `00…`), strip that
 * prefix out of the national number. When the country code that follows matches
 * one of the available countries, the matched country is returned so the caller
 * can switch the selector to it; otherwise `country` is null (leave the current
 * selection unchanged). Returns null when there is no international prefix, so
 * the caller knows to leave the input untouched.
 */
export function normalizeInternationalPrefix(
  raw: string,
  countries: Country[]
): { country: Country | null; national: string } | null {
  const withoutEscape = /^\s*\+/.test(raw)
    ? raw.replace(/^\s*\+/, "")
    : /^\s*00/.test(raw)
      ? raw.replace(/^\s*00/, "")
      : null;
  if (withoutEscape === null) return null;

  // Longest dial code (by digit count) whose digits lead the remaining number.
  const restDigits = withoutEscape.replace(/\D/g, "");
  let match: Country | null = null;
  let matchLen = 0;
  for (const c of countries) {
    const cd = c.dialCode.replace(/\D/g, "");
    if (cd && restDigits.startsWith(cd) && cd.length > matchLen) {
      match = c;
      matchLen = cd.length;
    }
  }

  // No known country code: clear the escape, keep the rest as typed.
  if (!match) return { country: null, national: withoutEscape };

  // Drop the matched dial-code digits from the (possibly formatted) remainder.
  let remaining = matchLen;
  let i = 0;
  for (; i < withoutEscape.length && remaining > 0; i++) {
    if (/\d/.test(withoutEscape[i])) remaining--;
  }
  return { country: match, national: withoutEscape.slice(i) };
}
